import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Save, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  code: 'BUILDER15',
  percent_off: 15,
  enabled: true,
  headline: 'A little gift for designing yours',
  subhead:
    'Use the code below at checkout for {percent}% off your first bracelet — yours, hand-strung by Lindy.',
};

export default function DiscountsAdminPage() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchDiscount = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/discounts/builder`);
      setForm({
        code: data.code || DEFAULTS.code,
        percent_off: typeof data.percent_off === 'number' ? data.percent_off : DEFAULTS.percent_off,
        enabled: !!data.enabled,
        headline: data.headline || DEFAULTS.headline,
        subhead: data.subhead || DEFAULTS.subhead,
      });
      setUpdatedAt(data.updated_at || null);
    } catch (err) {
      toast.error('Failed to load discount settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscount();
  }, []);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async (e) => {
    e?.preventDefault?.();
    const code = (form.code || '').trim().toUpperCase();
    if (code.length < 2) {
      toast.error('Code must be at least 2 characters');
      return;
    }
    const pct = parseInt(form.percent_off, 10);
    if (Number.isNaN(pct) || pct < 0 || pct > 80) {
      toast.error('Percent off must be between 0 and 80');
      return;
    }
    if (!form.headline.trim() || !form.subhead.trim()) {
      toast.error('Headline and subheading are required');
      return;
    }
    try {
      setSaving(true);
      const { data } = await axios.put(`${API}/discounts/builder`, {
        code,
        percent_off: pct,
        enabled: !!form.enabled,
        headline: form.headline.trim(),
        subhead: form.subhead.trim(),
      });
      setUpdatedAt(data.updated_at || new Date().toISOString());
      toast.success('Discount saved');
    } catch (err) {
      toast.error('Failed to save discount');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setForm(DEFAULTS);

  const previewSubhead = (form.subhead || '').replace(/\{percent\}/g, `${form.percent_off || 0}`);

  return (
    <div className="pt-28 pb-16 min-h-screen" data-testid="discounts-admin-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-5xl mx-auto">
        <AdminBreadcrumb current="Discounts" />
        <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-caption text-[#84A98C] mb-2">Admin · Discounts</p>
            <h1 className="heading-display text-3xl md:text-4xl text-[#354F52]">Builder Discount</h1>
            <p className="text-[#52796F] mt-3 max-w-xl">
              This popup is shown to customers on the Bracelet Builder right after their lifestyle photo
              has rendered. Set the code, the % off, and the copy below.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              className="flex items-center gap-2"
              data-testid="discounts-admin-reset"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
              Reset to defaults
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Form */}
          <form onSubmit={handleSave} className="lg:col-span-2 space-y-6 bg-[#F5F5F0] p-8 rounded-sm">
            <div className="flex items-center justify-between p-4 bg-white rounded-sm">
              <div>
                <Label className="text-[#354F52] text-base">Popup enabled</Label>
                <p className="text-xs text-[#84A98C] mt-1">
                  When off, the popup never shows and the code can't be redeemed at checkout.
                </p>
              </div>
              <Switch
                checked={!!form.enabled}
                onCheckedChange={(v) => set('enabled', v)}
                data-testid="discounts-admin-enabled"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-[#354F52]">Discount code</Label>
                <Input
                  id="code"
                  value={form.code}
                  onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s+/g, ''))}
                  maxLength={24}
                  className="uppercase tracking-widest font-serif text-lg border-[#CAD2C5]"
                  data-testid="discounts-admin-code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="percent" className="text-[#354F52]">Percent off (%)</Label>
                <Input
                  id="percent"
                  type="number"
                  min={0}
                  max={80}
                  value={form.percent_off}
                  onChange={(e) => set('percent_off', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  className="border-[#CAD2C5]"
                  data-testid="discounts-admin-percent"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="headline" className="text-[#354F52]">Headline</Label>
              <Input
                id="headline"
                value={form.headline}
                onChange={(e) => set('headline', e.target.value)}
                maxLength={120}
                className="border-[#CAD2C5]"
                data-testid="discounts-admin-headline"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subhead" className="text-[#354F52]">
                Subheading <span className="text-[#84A98C] text-xs">(use {`{percent}`} to insert the % value)</span>
              </Label>
              <Textarea
                id="subhead"
                value={form.subhead}
                onChange={(e) => set('subhead', e.target.value)}
                maxLength={280}
                rows={3}
                className="border-[#CAD2C5]"
                data-testid="discounts-admin-subhead"
              />
            </div>

            <div className="pt-2 flex items-center justify-between">
              <p className="text-xs text-[#84A98C]">
                {updatedAt ? `Last saved: ${new Date(updatedAt).toLocaleString('en-GB')}` : 'Not saved yet'}
              </p>
              <Button
                type="submit"
                disabled={saving || loading}
                className="btn-primary flex items-center gap-2"
                data-testid="discounts-admin-save"
              >
                <Save className="w-4 h-4" strokeWidth={1.5} />
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>

          {/* Live preview */}
          <div className="lg:col-span-1">
            <p className="text-caption text-[#84A98C] mb-3">Live preview</p>
            <div className="bg-[#FAF9F6] rounded-sm shadow-lg overflow-hidden border border-[#CAD2C5]/40">
              <div className="p-7 text-center">
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <p className="text-caption text-[#84A98C] mb-2">
                  {form.percent_off || 0}% OFF · YOUR DESIGN
                </p>
                <h3 className="font-serif text-xl text-[#354F52] mb-2 leading-tight">
                  {form.headline || '—'}
                </h3>
                <p className="text-[#52796F] text-sm mb-5">{previewSubhead || '—'}</p>
                <div className="bg-[#F5F5F0] border border-dashed border-[#84A98C] rounded-sm p-4 mb-3">
                  <p className="text-caption text-[#84A98C] mb-1">Your code</p>
                  <p className="font-serif text-2xl text-[#354F52] tracking-widest">
                    {(form.code || '').toUpperCase() || '—'}
                  </p>
                </div>
                <p className="text-xs text-[#84A98C]">
                  Apply <span className="font-medium text-[#354F52]">{(form.code || '').toUpperCase() || '—'}</span> at checkout.
                </p>
              </div>
            </div>
            {!form.enabled && (
              <p className="text-xs text-[#BC4749] mt-3 text-center">
                Popup is currently <strong>disabled</strong> — customers will not see this.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
